import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiArrowRight, PiPlayCircle } from "react-icons/pi";

export default function SplitHero({ content, onContentChange, isSelected }: SectionContentProps) {
    const primaryColor = content.colorPalette?.primary || "var(--primary-color, #3B82F6)";
    const secondaryColor = content.colorPalette?.secondary || "var(--secondary-color, #4F46E5)";
    const fontFamily = content.fontFamily || "'Inter', sans-serif";
    const padding = content.padding || "normal";
    const isReversed = content.imagePosition === "left" || content.align === "left";

    // Background style
    const bgStyle = content.bgStyle || "gradient";
    const getBgStyle = () => {
        if (content.backgroundColor) return { backgroundColor: content.backgroundColor };
        if (bgStyle === "solid") return { backgroundColor: "#ffffff" };
        if (bgStyle === "pattern") {
            return {
                backgroundColor: "#ffffff",
                backgroundImage: `radial-gradient(${primaryColor}30 1px, transparent 1px)`,
                backgroundSize: "24px 24px",
            };
        }
        const gradientStart = primaryColor.startsWith('var')
            ? `color-mix(in srgb, ${primaryColor}, transparent 92%)`
            : `${primaryColor}08`;

        return {
            background: `linear-gradient(135deg, ${gradientStart} 0%, #ffffff 100%)`,
        };
    };

    const getPaddingClass = () => {
        if (padding === "compact") return "py-16";
        if (padding === "spacious") return "py-32";
        return "py-24";
    };

    return (
        <div
            className={`relative overflow-hidden ${getPaddingClass()} ${isSelected ? "ring-2 ring-blue-500" : ""}`}
            style={{ ...getBgStyle(), fontFamily }}
        >
            <div className="container mx-auto px-6">
                <div className={`flex flex-col ${isReversed ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-12 md:gap-20`}>
                    <div className="flex-1 text-left">
                        <span className="inline-block py-1 px-3 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-6">
                            <EditableText
                                value={content.tag || "New Feature"}
                                onChange={(val) => onContentChange?.({ ...content, tag: val })}
                                as="span"
                            />
                        </span>
                        <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 leading-tight mb-6">
                            <EditableText
                                value={content.title || "Split Hero Title"}
                                onChange={(val) => onContentChange?.({ ...content, title: val })}
                                as="span"
                            />
                            <span className="block text-transparent bg-clip-text" style={{ backgroundImage: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})` }}>
                                <EditableText
                                    value={content.highlightText || ""}
                                    onChange={(val) => onContentChange?.({ ...content, highlightText: val })}
                                    as="span"
                                    placeholder="Highlight"
                                />
                            </span>
                        </h1>
                        <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed max-w-lg">
                            <EditableText
                                value={content.subtitle || "This is a descriptive subtitle for the split hero section. It allows for more text."}
                                onChange={(val) => onContentChange?.({ ...content, subtitle: val })}
                                as="span"
                            />
                        </p>

                        <div className="flex flex-wrap gap-4">
                            <a
                                href={onContentChange ? undefined : (content.primaryCtaLink || "#")}
                                target={content.primaryCtaLink?.startsWith('http') && !onContentChange ? "_blank" : undefined}
                                rel={content.primaryCtaLink?.startsWith('http') && !onContentChange ? "noopener noreferrer" : undefined}
                                onClick={(e) => onContentChange && e.preventDefault()}
                                className="px-8 py-4 rounded-xl font-bold text-white shadow-lg transition-transform hover:-translate-y-1 inline-flex cursor-pointer"
                                style={{ background: primaryColor }}
                            >
                                <EditableText
                                    value={content.primaryCta || "Get Started"}
                                    onChange={(val) => onContentChange?.({ ...content, primaryCta: val })}
                                    as="span"
                                />
                            </a>
                            <a 
                                href={onContentChange ? undefined : (content.secondaryCtaLink || "#")}
                                target={content.secondaryCtaLink?.startsWith('http') && !onContentChange ? "_blank" : undefined}
                                rel={content.secondaryCtaLink?.startsWith('http') && !onContentChange ? "noopener noreferrer" : undefined}
                                onClick={(e) => onContentChange && e.preventDefault()}
                                className="px-8 py-4 rounded-xl font-bold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 flex items-center gap-2 transition-colors cursor-pointer inline-flex"
                            >
                                <PiPlayCircle className="w-5 h-5" />
                                <EditableText
                                    value={content.secondaryCta || "Watch Video"}
                                    onChange={(val) => onContentChange?.({ ...content, secondaryCta: val })}
                                    as="span"
                                />
                            </a>
                        </div>

                        {(content.usersText !== "" || content.ratingText !== "") && (
                            <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 text-sm text-gray-500">
                                {content.usersText !== "" && (
                                    <div className="flex items-center gap-3">
                                        <div className="flex -space-x-2">
                                            {[1, 2, 3, 4].map(i => (
                                                <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-gray-200" style={{ backgroundColor: primaryColor, opacity: 0.2 + i * 0.1 }} />
                                            ))}
                                        </div>
                                        <p className="font-medium text-gray-600">
                                            <EditableText
                                                value={content.usersText ?? "2,500+ users"}
                                                onChange={(val) => onContentChange?.({ ...content, usersText: val })}
                                                as="span"
                                            />
                                        </p>
                                    </div>
                                )}
                                {(content.usersText !== "" && content.ratingText !== "") && (
                                    <div className="hidden sm:block w-px h-6 bg-gray-200" />
                                )}
                                {content.ratingText !== "" && (
                                    <div className="flex items-center gap-1.5">
                                        <svg
                                            className="w-4 h-4 text-yellow-400"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                        <span className="font-medium text-gray-600">
                                            <EditableText
                                                value={content.ratingText ?? "4.9/5 rating"}
                                                onChange={(val) => onContentChange?.({ ...content, ratingText: val })}
                                                as="span"
                                            />
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex-1 w-full relative">
                        <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white transform rotate-2 hover:rotate-0 transition-all duration-500 max-w-lg mx-auto">
                            {content.imageUrl || content.image ? (
                                <img
                                    src={content.imageUrl || content.image}
                                    className="w-full h-auto object-cover"
                                    alt="Hero"
                                />
                            ) : (
                                <div className="aspect-[4/3] bg-gray-100 flex items-center justify-center text-gray-400">
                                    No Image
                                </div>
                            )}
                        </div>
                        {/* Decorative blob */}
                        <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-tr from-blue-100 to-purple-100 blur-3xl opacity-50 rounded-full" />
                    </div>
                </div>
            </div>
        </div>
    );
}
